import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import PlusMinusInput from "components/Form/PlusMinusInput/PlusMinusInput";
import DeleteDialog from "components/Dialogs/DeleteDielog";
import useAPI from "hooks/useAPI";
import { currencyFormat } from "helpers/functions";
import { isPriceVisible, isPurchaseAllowed } from "helpers/product";
import styles from "./CheckoutTable.module.scss";

const ProductRow = ({ item, translateKey }) => {
    const { t } = useTranslation();
    const api = useAPI();
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showDialog, setShowDialog] = useState(false);
    const product = item?.product;
    const productId = product?.basic_data?.id_product ?? product?.id;
    const currency = product?.price?.currency;
    const rebate = product?.price?.per_item?.rebate;
    const discount = product?.price?.per_item?.discount;
    const rebateNames = rebate?.rebates?.map((entry) => entry?.calc?.calc_name).filter(Boolean).join("; ");
    const priceVisible = isPriceVisible(product?.behaviours?.checkout);
    const canPurchase = isPurchaseAllowed(product?.behaviours?.checkout);
    const formatPrice = (value) => priceVisible ? currencyFormat(value, currency) : t("global-default-text-price_on_request", "Cena na upit");

    const setQuantity = (quantity) => {
        api.globalAddToCart(productId, quantity, loading, setLoading, loadingDelete, setLoadingDelete, null, "replace");
    };

    const priceDetails = (
        <div className={styles.priceDetails}>
            <div>Regularna cena: {currencyFormat(product?.price?.per_item?.price_with_out_vat, currency)}</div>
            {rebate?.active && rebate?.rebates?.map((entry, index) => (
                <div key={`${entry?.calc?.calc_name}-${index}`}>Cena sa rabatom ({entry?.calc?.calc_name}): {currencyFormat(entry?.calc?.price, currency)}</div>
            ))}
            {discount?.active && discount?.campaigns?.map((campaign, index) => (
                <div key={`${campaign?.calc?.calc_name}-${index}`}>Cena sa popustom ({campaign?.calc?.calc_name}): {currencyFormat(campaign?.calc?.price, currency)}</div>
            ))}
        </div>
    );

    return (
        <>
            <tr aria-busy={loading || loadingDelete} className={loadingDelete ? styles.busyRow : undefined}>
                <td className={styles.tcCode}>{product?.basic_data?.sku}</td>
                <td className={styles.tcTitle}>
                    <Link to={`/katalog/artikal/${product?.id}`} className={styles.productName}>{product?.basic_data?.name}</Link>
                    {product?.basic_data?.attributes_text && <span className={styles.attributes}>{product.basic_data.attributes_text}</span>}
                    {!canPurchase && <span className={styles.unavailable}>{t("cart_checkout-default-text-product_purchase_not_allowed", "Artikal trenutno nije moguće kupiti.")}</span>}
                </td>
                <td className={styles.money}>{formatPrice(product?.price?.per_item?.price_with_out_vat)}</td>
                <td>{rebate?.active && rebateNames ? rebateNames : "0"}</td>
                <td className={styles.money}>{!priceVisible ? formatPrice(0) : discount?.active && Number(discount.amount) > 0 ? `-${formatPrice(discount.amount)}` : "0"}</td>
                <td className={styles.money}>
                    <Tooltip title={priceVisible ? priceDetails : ""} placement="top" arrow>
                        <span className={priceVisible ? styles.priceHint : undefined}>{formatPrice(product?.price?.per_item?.price_subtotal)}</span>
                    </Tooltip>
                </td>
                <td className={styles.tcInt}>
                    {canPurchase ? (
                        <>
                            <label className={styles.visuallyHidden} htmlFor={`${product?.id}_web`}>{t(`${translateKey}-quantity`)}</label>
                            <PlusMinusInput
                                behaviours={product?.behaviours?.checkout}
                                max={Number(product?.inventory?.amount)}
                                quantity={Number(item?.cart?.quantity ?? 0)}
                                setQuantity={setQuantity}
                                debounceId={`${product?.id}_web`}
                                className={styles.quantityInput}
                            />
                        </>
                    ) : Number(item?.cart?.quantity ?? 0)}
                </td>
                <td className={styles.money}>{formatPrice(product?.price?.cost?.subtotal)}</td>
                <td className={styles.tcActions}>
                    <button type="button" className={styles.removeButton} onClick={() => setShowDialog(true)} aria-label={`Izbriši ${product?.basic_data?.name}`} title="Izbriši">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M4.5 7h15M9.5 7V4.5h5V7M6.5 7l.7 12a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4l.7-12M10 10.5v6M14 10.5v6" />
                        </svg>
                    </button>
                </td>
            </tr>
            <DeleteDialog
                title="global-header-cart-delete_dialog-title"
                description="global-header-cart-delete_dialog-description"
                openDeleteDialog={{ show: showDialog }}
                setOpenDeleteDialog={() => setShowDialog(false)}
                handleConfirm={() => {
                    setLoadingDelete(true);
                    api.globalRemoveFromCheckout(item?.cart?.cart_item_id, loading, setLoading, loadingDelete, setLoadingDelete);
                    setShowDialog(false);
                }}
            />
        </>
    );
};

export default ProductRow;
